import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';


import {
  Alert,
  AlertTitle,
  Backdrop,
  Button,
  DialogActions,
  FormControlLabel,
  Grid,
  SvgIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {Box, Stack} from '@mui/system';
import {Empty} from '@zoom/react-ui';
import {IconProfileAddContact} from '@zoom/sun-ui-icon';
import {IconInformation} from '@zoom/sun-ui-icon/dist/cjs/index.js';
import {addMinutes} from 'date-fns';
import qs from 'querystringify';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'Store/Store';

import BookingFormContext from './BookingFormContext';
import PayButBookingError from './PayButBookingError';
import PaymentForm from './PaymentForm';
import RedirectDialog from './RedirectDialog';
import {RescheduleVotingForm} from './RescheduleVotingForm';
import {
  EventInfoContainer,
} from './Style';
import {VerificationForm} from './VerificationForm';
import {formatBookAndRescheduleLocation, MAX_BOOKER_NAME_LEN, MAX_EMAIL_LEN} from './yupSchema';

import CustomValues from '../CustomValuesWidget/index';


import {bookAppointmentSlot, bookVotingPoll, validateBookAppointmentSlot} from 'Api/Calendar';
import EmptyGlass from 'assets/png/emptyGlass.png';
import {ReactComponent as ArrowLeftOutlined} from 'assets/svg/arrow-left-outlined.svg';
import {VIEW_STATE} from 'Components/Appointments';
import ApptsContext from 'Components/Appointments/ApptsContext';
import ApptCard from 'Components/ApptCard';
import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import CountryInput from 'Components/CountryInput';
import EventInfo from 'Components/EventInfo';
import {LocationRadioSelector} from 'Components/Location/LocationRadioSelector';
import {RhfWatcher} from 'Components/RhfUtils';
import {ZmPrimaryBtn, ZmSecondaryBtn, ZmQuaternaryBtn} from 'Components/ZmButtons';
import {ZmCheckbox} from 'Components/ZmCheckbox/index';
import {ZmMultiEmail, ZmTextField} from 'Components/ZmInputs';
import {ZmSwitch} from 'Components/ZmInputs/index.js';
import ZmSpinner from 'Components/ZmSpinner';
import {ZmTooltip} from 'Components/ZmTooltip/ZmTooltip';
import {CaptchaError} from 'Error/CaptchaError';
import {HttpError} from 'Error/HttpError';
import {resetApptsDetailsState, setRoutingMetricState} from 'Store/BookAppointmentsStore';
import {NWS_ERROR_CODE} from 'Store/IntegrationStore';
import {addParamsToUrl, formatTimeIntervalLongDT, isCciCalendar} from 'Utils';
import {isAnonymousTeamAppt} from 'Utils/apptAvailabilityUtils';
import {useQueryWithCaptchaPasscode} from 'Utils/Captcha';
import {isPaidAppointment} from 'Utils/CardDisplay';
import {ADHOC_TYPE, CRM_LABEL_MAPPING, LOCAL_STORAGE_KEYS, UTM_LABEL_MAPPING} from 'Utils/consts';
import {encryptAES} from 'Utils/crypto';
import {formatCustomValues} from 'Utils/customValueUtils';
import {formatEventInfo} from 'Utils/eventUtils';
import {useRecommendAvailableSlots} from 'Utils/hooks';
import zoomPalette from 'zoomPalette';

import 'react-multi-email/dist/style.css';


/**
 * Booking Form Component -
 *   Renders the form to intake the user's name and email and manages form submission
 * @param {Object} props
 * @param {Object} props.appointment the top level appt object, used to display information
 * @param {string} props.calendarId
 * @param {function} props.onBook callback on successful book response
 * @param {function} props.onCancel callback on dialog close
 * @param {Date} props.startDateTime slot start time to book
 * @return {ReactElement}
 */
export function BookingFormContent(props) {
  const {
    appointment,
    appointmentSmsEnabled,
    calendarId,
    onBook,
    onCancel,
    prefillMode,
    showAutoRefundPolicy,
    startDateTime,
    timeZone,
  } = props;
  const {
    displayOtp,
    errorState = {},
    isSubmitting,
    setDisplayOtp,
    setErrorState,
    setIsSubmitting,
    setShowGuestEmails,
    showGuestEmails = true,
  } = useContext(BookingFormContext) || {};

  const {
    isBookerVoting,
    votes,
  } = useContext(ApptsContext) || {};

  const {
    control,
    formState: {errors},
    getValues,
    handleSubmit,
    register,
    setValue,
    trigger,
    watch,
  } = useFormContext();
  const {t} = useTranslation();
  const theme = useTheme();
  const [guestsFocused, setGuestsFocused] = useState(false);
  const cfg = watch('locationConfiguration');
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchParams] = useSearchParams();
  const onlyShowSelectSlot = !!searchParams.get('slt');
  const {availableTimeSlots, isCurrentSlotAvailable} = useRecommendAvailableSlots();
  const allTimeSlotsTaken = onlyShowSelectSlot && !isCurrentSlotAvailable && !availableTimeSlots.length;

  const createrDisplayName = useAppSelector((s) => s.bookAppointments.createrDisplayName);

  const isCciAccount = isCciCalendar(appointment?.organizer?.email);

  const isPaymentAppt = isPaidAppointment(appointment);

  let submitBtnText = isBookerVoting ? t('polls.vote') : t('booking.book');
  if (isPaymentAppt) {
    submitBtnText = t('common.continue');
  }

  const clearOnCancel = () => {
    setErrorState({
      isError: false,
      reason: '',
    });
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    handleSubmit(async (formValues) => {
      if (appointment.verify && !displayOtp) {
        clearOnCancel();
        setDisplayOtp(true);
        setIsSubmitting(false);
      } else {
        try {
          await onBook(formValues);
          // after booked success, current verification code is not valid anymore
          window.sessionStorage.removeItem('verifySentTime');
          // after booked success, remove pre-populated form values
          window.sessionStorage.removeItem('formContext');
        } catch (e) {
          console.error('Encounter unexpected error when booking appointment:', e);
        } finally {
          setIsSubmitting(false);
        }
      }
    }, (e) => {
      console.error('booking form validation error', e);
      setIsSubmitting(false);
    })();
  };

  return (
    <>
      <Backdrop
        sx={{
          position: 'absolute',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
        open={isSubmitting}
      >
        <ZmSpinner size="80" absolute />
      </Backdrop>

      {
        allTimeSlotsTaken? <Empty
          image={EmptyGlass}
          imageStyles={{
            margin: '0 auto',
            width: 360,
          }}
          description={t('booking.emailAllTimeSlotsTaken')}
        />:
      <>{!displayOtp ?
        <>
          <Box>
            {errorState.isError &&
              <Alert id="booking-error-alert" severity="error" sx={{marginBottom: '16px', borderRadius: '10px'}}>
                <AlertTitle>{t('booking.errorTitle')}</AlertTitle>
                {errorState.reason}
              </Alert>
            }
            {!isCurrentSlotAvailable && !!availableTimeSlots.length && <Alert
              sx={{marginBottom: '16px', borderRadius: '10px'}} severity="error">
              <Stack direction="column">
                <div>{t('booking.emailTimeSlotOccupiedError')}</div>
                <div style={{marginTop: '10px'}}>{t('booking.selectAnotherAvailableTime')}:</div>
                {availableTimeSlots.map((slot) => {
                  return <Button key={slot} sx={{margin: '5px 0', fontSize: '.84rem'}} size="small" variant="outlined"
                    onClick={() => {
                      window.sessionStorage.setItem('formContext', JSON.stringify(getValues()));
                      window.location.href = addParamsToUrl(window.location.href, {
                        slt: encryptAES(slot),
                      });
                    }}>
                    {formatTimeIntervalLongDT(
                      toDateTime(slot, timeZone),
                      toDateTime(addMinutes(new Date(slot), appointment.duration), timeZone))
                    }
                  </Button>;
                })}
              </Stack>
            </Alert>}
            {!prefillMode && <EventInfoContainer>
              <EventInfo
                appointment={appointment}
                displayName={createrDisplayName}
                showAvatar={!isAnonymousTeamAppt(appointment)}
                startDateTime={startDateTime}
                timeZone={timeZone}
                isBookerVoting={isBookerVoting}
                votes={votes}
                showAutoRefundPolicy={showAutoRefundPolicy}
              />
            </EventInfoContainer>}

          </Box>

          <Box>
            {!prefillMode && <div style={{display: 'flex', alignItems: 'center', marginBottom: '12px'}}>
              <Typography
                sx={{
                  display: 'inline-flex',
                  gap: '4px',
                  fontSize: '1rem',
                  fontWeight: 500,
                  lineHeight: '1.5rem',
                  marginRight: '4px',
                  color: theme.palette.text.primary,
                }}
              >
                {t('booking.enterInformation')}
              </Typography>
              <ZmTooltip
                title={t('booking.disclaimerText')}
                arrow
                placement="top">
                <IconInformation color={zoomPalette.gray[50]}/>
              </ZmTooltip>
            </div>}

            <ZmTextField
              autoFocus
              label={t('booking.firstName')}
              margin="dense"
              required={!prefillMode}
              id="bookerFirstName"
              type="text"
              fullWidth
              variant="outlined"
              aria-required
              inputProps={{...register('bookerFirstName'), maxLength: MAX_BOOKER_NAME_LEN}}
              aria-live="polite"
              error={!!errors.bookerFirstName?.message}
              helperText={errors.bookerFirstName?.message}
            />

            <ZmTextField
              label={t('booking.lastName')}
              margin="dense"
              required={!prefillMode}
              id="bookerLastName"
              type="text"
              fullWidth
              variant="outlined"
              aria-required
              inputProps={{...register('bookerLastName'), maxLength: MAX_BOOKER_NAME_LEN}}
              aria-live="polite"
              error={!!errors.bookerLastName?.message}
              helperText={errors.bookerLastName?.message}
            />

            <ZmTextField
              label={t('booking.emailAddress')}
              margin="dense"
              required={!prefillMode}
              id="bookerEmail"
              type="email"
              fullWidth
              variant="outlined"
              aria-required
              inputProps={{...register('bookerEmail'), maxLength: MAX_EMAIL_LEN}}
              aria-live="polite"
              error={!!errors.bookerEmail?.message}
              helperText={errors.bookerEmail?.message}
            />
            {appointment?.capacity === 1 &&
              (appointment?.guestsAllowed !== false) &&
                !isBookerVoting && <div
              style={{
                margin: '8px 0px',
              }}
            >
              {!showGuestEmails ?
                <ZmQuaternaryBtn
                  size="xsmall"
                  onClick={() => setShowGuestEmails(true)}
                  startIcon={<IconProfileAddContact />}
                >
                  {t('booking.addAttendees')}
                </ZmQuaternaryBtn> :
                <Controller
                  name="guests"
                  control={control}
                  render={({field, fieldState}) => (
                    <ZmMultiEmail
                      id="attendeeEmails"
                      label={<Typography variant="subtitle2">{t('booking.attendeeEmails')}</Typography>}
                      emails={field.value}
                      setEmails={field.onChange}
                      focused={guestsFocused}
                      onFocus={() => setGuestsFocused(true)}
                      onBlur={() => setGuestsFocused(false)}
                      error={!!fieldState.error?.message}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              }
            </div>}
            <LocationRadioSelector
              value={cfg}
              errors={errors}
              configurations={appointment?.locationConfigurations}
              onChange={(data) => {
                setValue('locationConfiguration', {
                  ...cfg,
                  ...data,
                });
                trigger('locationConfiguration');
              }}
            />
            {/* should always show this module for ZCC because ZCC does not involve sending SMS */}
            {(process.env.REACT_APP_ENABLE_SMS === 'true' || isCciAccount) &&
              <>
                {!isCciAccount && appointmentSmsEnabled && <Controller
                  name="bookerSendSmsUpdates"
                  control={control}
                  render={({field}) => (
                    <FormControlLabel
                      sx={{
                        margin: isSmallScreen? 0: null,
                        color: theme.palette.text.primary,
                      }}
                      control={
                        isSmallScreen ?
                        <ZmSwitch
                          size="small"
                          checked={field.value}
                          id="bookerSendSmsUpdatesVisible"
                          name="bookerSendSmsUpdatesVisible"
                          onChange={field.onChange}
                          sx={{
                            padding: 0,
                            marginRight: '8px',
                          }}
                        /> :
                        <ZmCheckbox
                          checked={field.value}
                          id="bookerSendSmsUpdatesVisible"
                          name="bookerSendSmsUpdatesVisible"
                          onChange={field.onChange}
                          sx={{
                            padding: 0,
                            marginRight: '8px',
                            marginLeft: '3px',
                          }}
                        />
                      }
                      label={t('booking.sendSmsUpdates')}
                    />
                  )}
                />}
                <RhfWatcher watchVals={['bookerSendSmsUpdates']}>
                  {(sendSmsUpdates) =>
                    ((sendSmsUpdates && appointmentSmsEnabled) || isCciAccount) && <>
                      <Grid container spacing={2}>
                        <Grid item style={{width: '140px'}}>
                          <Controller
                            name="bookerPhoneCountryCode"
                            control={control}
                            render={({field, fieldState}) => (
                              <CountryInput
                                id={'bookerPhoneCountryCode'}
                                value={field.value}
                                onChange={field.onChange}
                                aria-live="polite"
                                error={fieldState.error}
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs>
                          <Controller
                            name="bookerPhoneNumber"
                            control={control}
                            render={({field, fieldState}) => (
                              <ZmTextField
                                label={t('booking.phoneNumber')}
                                margin="dense"
                                id="bookerPhoneNumber"
                                type="text"
                                fullWidth
                                variant="outlined"
                                required={isCciAccount}
                                aria-required
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.replace(/[^\d]/g, ''))}
                                inputProps={{
                                  autoComplete: 'tel-national',
                                  minLength: 5,
                                  maxLength: 20,
                                }}
                                aria-live="polite"
                                error={!!fieldState.error?.message}
                                helperText={fieldState.error?.message}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      {!isCciAccount && <div
                        style={{color: theme.palette.text.tertiary, fontSize: '13px', padding: '8px 0 6px'}}>
                        {t('booking.unsubscribeSmsTip')}
                      </div>}
                    </>
                  }
                </RhfWatcher>
              </>
            }
            {!isCciAccount && !isBookerVoting && <CustomValues
              customFields={appointment?.customFields || []}
            />}
          </Box>

          {!prefillMode && <DialogActions sx={{
            marginY: '28px',
            paddingX: '0px',
          }}>
            {!isSmallScreen && !onlyShowSelectSlot && <ZmSecondaryBtn
              size="small"
              onClick={() => {
                onCancel();
                clearOnCancel();
              }}
              disabled={isSubmitting}
              sx={{height: '2rem'}}
            >
              {t('common.back')}
            </ZmSecondaryBtn>}
            <RhfWatcher
              watchVals={
                ['bookerEmail', 'bookerFirstName', 'bookerLastName', 'bookerSendSmsUpdates', 'bookerPhoneNumber']
              }
            >
              {(email, firstName, lastName, sendSmsUpdates, phoneNumber) => (
                <ZmPrimaryBtn
                  size="small"
                  onClick={onSubmit}
                  disabled={
                    (
                      !email ||
                      !firstName ||
                      !lastName ||
                      (isCciAccount && !phoneNumber) ||
                      isSubmitting
                    ) ?
                    true :
                    false
                  }
                  style={isSmallScreen ? {
                    width: '100%',
                  }: {}}
                  sx={{height: '2rem'}}
                >
                  {submitBtnText}
                </ZmPrimaryBtn>
              )}
            </RhfWatcher>
          </DialogActions>}
        </> :
        <RhfWatcher watchVals={['bookerEmail']}>
          {(email) =>
            <VerificationForm
              calendarId={calendarId}
              email={email}
              errorState={errorState}
              onCancel={() => {
                // onCancel();
                window.sessionStorage.removeItem('code');
                setDisplayOtp(false);
                clearOnCancel();
              }}
              onSubmit={(passcode) => {
                window.sessionStorage.setItem('code', passcode);
                onSubmit();
              }}
              isSubmitting={isSubmitting}
            />
          }
        </RhfWatcher>
      }</>}
    </>
  );
};

/**
 * Booking Finalizer Component -
 *   Renders the content in the right panel after
 *     user selects a time slot to intake the user's name and email, and show confirmation on success
 * @param {Object} props
 * @param {Object} props.appointment the top level appt object, used to display information
 * @param {string} props.calendarId
 * @param {function} props.onCancel callback on form Cancel or Back button click
 * @param {Date} props.startDateTime slot start time to book
 * @return {ReactElement}
 */
export default function BookingFinalizer(props) {
  const {
    appointment,
    appointmentSmsEnabled,
    calendarId,
    startDateTime,
  } = props;

  const {
    isBookerVoting,
    timeFormat,
    timeZone,
    voterInfo,
    votes,
  } = useContext(ApptsContext);

  const {
    confirmationState,
    currView,
    handleBack,
    setConfirmationState,
    setCurrView,
    setDisplayOtp,
    setErrorState,
  } = useContext(BookingFormContext);

  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [displayRedirectDialog, setDisplayRedirectDialog] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  // const [timerId, setTimerId] = useState(null);
  const [formValues, setFormValues] = useState(null);

  const [bookingError, setBookingError] = useState(null);

  const onlyShowSelectSlot = !!searchParams.get('slt');

  const isPaymentAppt = isPaidAppointment(appointment);

  useEffect(() => {
    if (currView === VIEW_STATE.CONFIRM_VIEW &&
      appointment?.confirmationPageType === 'external' &&
      appointment?.redirectConfigurationAttributes &&
      appointment?.redirectConfigurationAttributes?.url) {
      setDisplayRedirectDialog(true);
      let baseUrl = appointment?.redirectConfigurationAttributes?.url;
      if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
        baseUrl = 'https://' + baseUrl;
      }

      const params = {
        assignedTo: confirmationState.event?.assignedTo.join(','),
        eventId: confirmationState.event?.id,
        summary: confirmationState.event?.summary,
        start: confirmationState.event?.originalEvent.start.dateTime,
        end: confirmationState.event?.originalEvent.start.dateTime,
        timeZone: confirmationState.event?.timeZone,
        bookerDisplayName: confirmationState.event.bookerInfo?.displayName,
        bookerEmail: confirmationState.event.bookerInfo?.email,
        bookerId: confirmationState.event.bookerInfo?.id,
      };
      let paramString = '';
      if (appointment?.redirectConfigurationAttributes.passBookingDetails) {
        paramString = qs.stringify(params);
      }

      const url = `${baseUrl}${!baseUrl.endsWith('/') ? '/' : ''}?${paramString}`;
      try {
        new URL(url);
        // const id = setTimeout(() => {
        //   window.open(url, '_blank');
        // }, 5000); // 5 seconds
        // setTimerId(id);
        setRedirectUrl(url);
      } catch (error) {
        console.log('invalid redirect url', url);
      }
    }
  }, [appointment, confirmationState, currView]);

  const routingMetricState = useAppSelector((s) => s.bookAppointments.routingMetricState);

  const isCciAccount = isCciCalendar(appointment?.organizer?.email);

  const trackingParams = useMemo(() => {
    const params = {};

    Object.keys({...UTM_LABEL_MAPPING, ...CRM_LABEL_MAPPING}).forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams]);

  const routingFormMetricFields = useMemo(() => {
    const {form, responseId} = (routingMetricState ?? {});
    if (responseId && form) {
      return {
        responseId: responseId,
        form: form,
      };
    }
    return null;
  }, [routingMetricState]);

  const doBookFactory = (fetcher) => async (calendarId, appointmentId, formValues, passcode, bizOrderId,
    recaptchaToken) => {
    const customArr = formatCustomValues(formValues, appointment?.customFields);
    // console.log(customArr);
    // server will provide a localized email based on the booker's preferred language
    const language =
      localStorage.getItem(LOCAL_STORAGE_KEYS.BOOKING_PAGE_I18NEXT_LNG) ||
      localStorage.getItem(LOCAL_STORAGE_KEYS.I18NEXT_LNG) || navigator.language;
    return await fetcher(calendarId, appointmentId, routingFormMetricFields, {
      bizOrderId,
      start: {
        dateTime: startDateTime.toISOString(),
        timeZone: timeZone,
      },
      duration: appointment.duration,
      booker: {
        email: formValues.bookerEmail,
        firstName: formValues.bookerFirstName,
        lastName: formValues.bookerLastName,
        language,
        timeFormat,
        ...((formValues.bookerSendSmsUpdates || isCciAccount) && formValues.bookerPhoneNumber && {
          phoneNumber: formValues.bookerPhoneCountryCode.split('-')[0] + ' ' + formValues.bookerPhoneNumber,
        }),
      },
      customValues: customArr,
      recaptchaToken,
      ...(passcode && {verificationCode: passcode}),
      ...(formValues.guests && {guests: formValues.guests.map((email) => ({email}))}),
      tracking: trackingParams,
      ...formatBookAndRescheduleLocation(formValues),
    });
  };

  const doBook = useQueryWithCaptchaPasscode(doBookFactory(bookAppointmentSlot));
  const doValidateBookSlot = useQueryWithCaptchaPasscode(doBookFactory(validateBookAppointmentSlot));

  const doVote = useQueryWithCaptchaPasscode(async (calendarId, formValues, recaptchaToken) => {
    const votesArr = Object.values(votes);
    const spots = votesArr.map((vote) => {
      return {
        start: vote.start.toISOString(),
      };
    });

    return await bookVotingPoll(calendarId, {
      voter: {
        email: formValues.bookerEmail,
        name: `${formValues.bookerFirstName} ${formValues.bookerLastName}`,
      },
      spots: spots,
      timeZone: timeZone,
      recaptchaToken,
    });
  });

  const postFormInfoToParentPage = (formValues, bookingResponse) => {
    const origin = searchParams.get('origin');
    console.log('formValues', origin, formValues);

    if (!origin || !window?.parent) {
      return;
    }

    let scheduledEventData = {};
    const attendee = (bookingResponse?.attendees ?? []).find((attendee) => attendee.email === formValues.bookerEmail);
    if (bookingResponse?.id && attendee?.id) {
      scheduledEventData = {
        scheduledEventId: bookingResponse.id,
        attendeeId: attendee.id,
      };
    }
    window.parent.postMessage(
      {
        type: 'bookingForm',
        payload: {
          // New: Only provide event and attendee ids.
          ...scheduledEventData,
          // Old: Will deprecate
          ...formValues,
        },
      },
      origin,
    );
  };

  const handleOnBook = useCallback((bookedEvent, userEmail) => {
    // navigate to final booked event viewer
    console.log('booking event', bookedEvent);
    // reset the appt instance id details state so UI will refetch appt instance's free-busy data
    dispatch(resetApptsDetailsState());
    if (bookedEvent?.bookingMethod === ADHOC_TYPE.POLL) {
      // For POLL voting, don't format the event as the data type is different.
      setConfirmationState({
        event: bookedEvent,
        userEmail: userEmail,
      });
    } else {
      setConfirmationState({
        event: formatEventInfo(
          bookedEvent,
          (event) => event?.attendees?.find((attendee) => attendee.email === userEmail),
        ),
        userEmail: userEmail,
      });
    }
    setCurrView(VIEW_STATE.CONFIRM_VIEW);
  }, [dispatch, setConfirmationState, setCurrView]);

  const handleBook = async (formValues, extraParams = {}) => {
    const {bizOrderId, skipPayment = false} = extraParams;
    try {
      let bookedEvent;
      setFormValues(formValues);
      if (isPaymentAppt && !skipPayment) {
        setCurrView(VIEW_STATE.PAYMENT_VIEW);
        return;
      } else if (isBookerVoting) {
        bookedEvent = await doVote(calendarId, formValues);
      } else if (!appointment.verify) {
        bookedEvent = await doBook(calendarId, appointment.id, formValues, '', bizOrderId);
      } else {
        bookedEvent = await doBook(
          calendarId,
          appointment.id,
          formValues,
          window.sessionStorage.getItem('code').toString(),
          bizOrderId,
        );
      }
      console.log('Booked appt', bookedEvent);
      postFormInfoToParentPage(formValues, bookedEvent);
      // After a successful booking, clear the routing form source state
      dispatch(setRoutingMetricState(null));
      setDisplayOtp(false);
      handleOnBook(bookedEvent, formValues.bookerEmail);
    } catch (e) {
      if (isPaymentAppt) {
        setCurrView(VIEW_STATE.PAYMENT_BUT_BOOKING_ERROR_VIEW);
        setBookingError(e);
        return;
      }
      console.error('Failed to book:', e, e.errorCode, e.message);
      let reason = '';
      if (e instanceof CaptchaError) {
        reason = t('booking.captchaPrompt');
      } else if (e instanceof HttpError) {
        const errMsg = (e.body?.error?.message) || '';
        switch (e.errorCode) {
          case 400:
            if (errMsg.includes('verification code')) {
              reason = t('booking.incorrectPasscode');
            } else if (onlyShowSelectSlot && errMsg.includes('Requested time slot busy')) {
              reason = t('booking.emailTimeSlotOccupiedError');
            } else {
              setDisplayOtp(false);
              reason = errMsg || t('booking.genericHttpValidationError');
            }
            break;
          case 401:
            setDisplayOtp(false);
            reason = errMsg || 'Unauthorized';
            break;
          case 403:
            setDisplayOtp(true);
            reason = t('booking.incorrectPasscode');
            window.sessionStorage.removeItem('code');
            break;
          case 404:
            setDisplayOtp(false);
            reason = t('error.errorMsgBody404');
            break;
          case 500:
            setDisplayOtp(false);
            reason = t('error.errorMsgBody500');
            if ([NWS_ERROR_CODE.INVALID_GRANT,
              'Invalid Credentials', 'connection is empty'].some((item) => e.message?.includes(item))) {
              reason = t('booking.hostAccountInvalid');
            }
            if (errMsg.includes('Permission deny')) {
              reason = t('booking.hostAccountInvalid');
            }
            // failed to create Meeting link, show the reason let user know what happened
            if (errMsg && (
              errMsg.startsWith('ZoomMeetingError') ||
              errMsg.startsWith('GoogleMeetError') ||
              errMsg.startsWith('TeamsMeetingError')
            )) {
              reason = errMsg;
            }
            break;
          default:
            setDisplayOtp(false);
            reason = t('booking.genericHttpError');
            break;
        }
      } else {
        reason = t('booking.genericError');
      }
      setErrorState({
        isError: true,
        reason: reason,
      });

      setTimeout(() => {
        // ensure error alert is displayed within the user's viewport when the page height is high
        document.querySelector('#booking-error-alert')?.scrollIntoView({behavior: 'smooth'});
      }, 200);

      // to avoid viewing the error on OTP screen load
      // if (e.errorCode === 403) {
      //   clearOnCancel();
      // }
    }
  };

  const containerStyles = useMemo(() => (currView === VIEW_STATE.PAYMENT_VIEW ?
    {margin: 'auto'} :
    {maxWidth: '500px', margin: 'auto'}),
  [currView]);

  return (
    <Box sx={containerStyles}>
      {!isSmallScreen && currView === VIEW_STATE.CONFIRM_VIEW &&
        !confirmationState?.event?.originalEvent?.fromSingleUseLink &&
          !isBookerVoting && <ZmSecondaryBtn
        variant="contained"
        color="inherit"
        size="small"
        sx={{
          'marginBottom': '24px',
        }}
        startIcon={<SvgIcon fontSize="xsmall" component={ArrowLeftOutlined} inheritViewBox />}
        onClick={handleBack}
        aria-label="Go back to availability viewer"
      >
        {t('common.back')}
      </ZmSecondaryBtn>}
      {(currView === VIEW_STATE.FORM_VIEW) && voterInfo &&
        <RescheduleVotingForm
          appointment={appointment}
          calendarId={calendarId}
          onBook={(bookedEvent, userEmail) => {
          // navigate to final booked event viewer
            console.log('voting event', bookedEvent);
            // reset the appt instance id details state so UI will refetch appt instance's free-busy data
            dispatch(resetApptsDetailsState());
            // For POLL voting, don't format the event as the data type is different.
            setConfirmationState({
              event: bookedEvent,
              userEmail: userEmail,
            });
            setCurrView(VIEW_STATE.CONFIRM_VIEW);
          }}
          onCancel={handleBack}
          startDateTime={startDateTime}
          timeZone={timeZone}
        />
      }
      {(currView === VIEW_STATE.FORM_VIEW) && !voterInfo &&
        <BookingFormContent
          appointment={appointment}
          appointmentSmsEnabled={appointmentSmsEnabled}
          calendarId={calendarId}
          onBook={handleBook}
          onCancel={handleBack}
          startDateTime={startDateTime}
          timeZone={timeZone}
          showAutoRefundPolicy={true}
        />
      }
      {(currView === VIEW_STATE.CONFIRM_VIEW) &&
        <ApptCard
          attendeeId={confirmationState?.event?.attendees?.find((attendee) => {
            return attendee.email === confirmationState?.userEmail;
          })?.id}
          confirmMessageKey={'booking.bookedWith'}
          displayName={confirmationState.event?.hostDisplayName}
          eventInfo={confirmationState.event}
          guests={confirmationState.event?.guests}
          timeZone={timeZone}
          anotherEventButtonEnabled={appointment.anotherEventButtonEnabled}
          anotherEventButtonContent={appointment.anotherEventButtonContent}
          isBookerVoting={isBookerVoting}
          votes={votes}
          displayPreferredTimes={true}
          showAutoRefundPolicy={true}
        />
      }

      {currView === VIEW_STATE.PAYMENT_VIEW && <PaymentForm
        calendarId={calendarId}
        appointment={appointment}
        formValues={formValues}
        startDateTime={startDateTime}
        onBook={handleBook}
        onValidateBookSlot={doValidateBookSlot}
      />}

      {currView === VIEW_STATE.PAYMENT_BUT_BOOKING_ERROR_VIEW && (
        <PayButBookingError
          calendarId={calendarId}
          appointment={appointment}
          bookingError={bookingError}
          timeZone={timeZone}
        />
      )}

      <RedirectDialog
        displayRedirectDialog={displayRedirectDialog}
        setDisplayRedirectDialog={setDisplayRedirectDialog}
        redirectUrl={redirectUrl}
        // clearTimeout={() => clearTimeout(timerId)}
      />
    </Box>
  );
}
